/**
 * 教师看板（数据口径：我教的课程）
 *
 * 布局：① 6 张 KPI ② 待办 + 我的课程学生数
 *      ③ 我的课程 · 考勤录入 + 活动组织
 *      ④ 教学运行：作业提交 / 课题研究（数据未录入时显示占位，结构已留好）
 *      ⑤ 我的课程列表
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card, Row, Col, Statistic, List, Tag, Spin, Typography, Empty, message, Button, Space, Progress, Tooltip,
} from 'antd';
import {
  BookOutlined, TeamOutlined, ExclamationCircleOutlined, FileTextOutlined,
  EditOutlined, ClockCircleOutlined, CalendarOutlined, RightOutlined,
} from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import { authApi } from '../../../services/api';
import type { TeacherDashboardSummary } from '../../../services/types';

const { Text } = Typography;

const TeacherOverview: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<TeacherDashboardSummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    authApi.getTeacherSummary()
      .then((res) => setData(res.data || null))
      .catch(() => message.error('加载看板数据失败'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>;
  }
  if (!data) {
    return <Empty description="看板数据加载失败，请刷新页面重试" />;
  }

  const { kpi, todos, teaching, my_courses: myCourses, activities } = data;

  const todoItems = [
    { key: 'pending_homework', label: '待批作业', count: todos.pending_homework, to: '/courses' },
    { key: 'pending_research', label: '待审课题材料', count: todos.pending_research, to: '/courses' },
    { key: 'sessions_without_attendance', label: '未录考勤课次', count: todos.sessions_without_attendance, to: '/courses' },
    { key: 'draft_grade_batches', label: '待录成绩批次', count: todos.draft_grade_batches, to: '/grades' },
    { key: 'pending_grade_review', label: '成绩待审核', count: todos.pending_grade_review, to: '/grades' },
  ];
  const todoTotal = todoItems.reduce((sum, item) => sum + item.count, 0);

  const kpiCards = [
    { title: '我的课程', value: kpi.my_courses, icon: <BookOutlined />, color: '#722ed1' },
    { title: '我的学生', value: kpi.my_students, icon: <TeamOutlined />, color: '#1677ff' },
    { title: '待批作业', value: kpi.pending_homework, icon: <ExclamationCircleOutlined />, color: kpi.pending_homework > 0 ? '#ff4d4f' : '#52c41a' },
    { title: '待审课题', value: kpi.pending_research, icon: <FileTextOutlined />, color: kpi.pending_research > 0 ? '#fa8c16' : '#52c41a' },
    { title: '待录成绩', value: kpi.draft_grade_batches, icon: <EditOutlined />, color: kpi.draft_grade_batches > 0 ? '#ff4d4f' : '#52c41a' },
    { title: '未录考勤课次', value: kpi.sessions_without_attendance, icon: <ClockCircleOutlined />, color: kpi.sessions_without_attendance > 0 ? '#fa8c16' : '#52c41a' },
  ];

  const courseChartData = myCourses.slice(0, 8);
  const courseOption = {
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
    grid: { left: 8, right: 36, top: 10, bottom: 8, containLabel: true },
    xAxis: { type: 'value', minInterval: 1, splitLine: { lineStyle: { color: '#f0f0f0' } }, axisLabel: { color: '#8c8c8c', fontSize: 11 } },
    yAxis: {
      type: 'category',
      inverse: true,
      data: courseChartData.map((c) => c.name),
      axisLabel: { fontSize: 11, color: '#595959', width: 110, overflow: 'truncate' },
      axisLine: { lineStyle: { color: '#e5e7eb' } },
    },
    series: [
      {
        name: '学生数',
        type: 'bar',
        barWidth: 12,
        data: courseChartData.map((c) => c.student_count),
        itemStyle: { color: '#722ed1', borderRadius: [0, 6, 6, 0] },
        label: { show: true, position: 'right', fontSize: 11, color: '#8c8c8c' },
      },
    ],
  };

  // 我的课程 · 考勤录入完成度（已录 / 未录 课次，横向堆叠条）
  const att = teaching.attendance;
  const attByCourse = att.by_course.slice(0, 8);
  const attendanceOption = {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params: any) => {
        const name = params[0]?.name ?? '';
        const done = params.find((p: any) => p.seriesName === '已录考勤')?.value ?? 0;
        const todo = params.find((p: any) => p.seriesName === '未录考勤')?.value ?? 0;
        return `${name}<br/>已录 ${done} 次 / 应上 ${done + todo} 次`;
      },
    },
    legend: { bottom: 0, itemWidth: 10, itemHeight: 10, textStyle: { fontSize: 12 } },
    grid: { left: 8, right: 48, top: 12, bottom: 34, containLabel: true },
    xAxis: {
      type: 'value',
      minInterval: 1,
      splitLine: { lineStyle: { color: '#f0f0f0' } },
      axisLabel: { color: '#8c8c8c', fontSize: 11 },
    },
    yAxis: {
      type: 'category',
      inverse: true,
      data: attByCourse.map((c) => c.name),
      axisLabel: { fontSize: 11, color: '#595959', width: 110, overflow: 'truncate' },
      axisLine: { lineStyle: { color: '#e5e7eb' } },
    },
    series: [
      {
        name: '已录考勤',
        type: 'bar',
        stack: 'attendance',
        barWidth: 14,
        data: attByCourse.map((c) => c.sessions_recorded),
        itemStyle: { color: '#52c41a', borderRadius: [6, 0, 0, 6] },
      },
      {
        name: '未录考勤',
        type: 'bar',
        stack: 'attendance',
        barWidth: 14,
        data: attByCourse.map((c) => Math.max(c.sessions_total - c.sessions_recorded, 0)),
        itemStyle: { color: '#f0f0f0', borderRadius: [0, 6, 6, 0] },
        label: {
          show: true,
          position: 'right',
          fontSize: 11,
          color: '#8c8c8c',
          formatter: (params: any) => {
            const row = attByCourse[params.dataIndex];
            return `${row.sessions_recorded}/${row.sessions_total}`;
          },
        },
      },
    ],
  };

  // 教学运行：作业 / 课题
  const hw = teaching.homework;
  const research = teaching.research;

  return (
    <>
      {/* ① KPI */}
      <Row gutter={[16, 16]}>
        {kpiCards.map((card) => (
          <Col key={card.title} xs={12} md={8} xl={4}>
            <Card style={{ height: '100%' }}>
              <Statistic title={card.title} value={card.value} prefix={card.icon} styles={{ content: { color: card.color, fontSize: 26 } }} />
            </Card>
          </Col>
        ))}
      </Row>

      {/* ② 待办 + 我的课程学生数 */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={14}>
          <Card
            title="待办事项"
            size="small"
            style={{ height: '100%' }}
            extra={todoTotal > 0
              ? <Tag color="red">{todoTotal} 项待处理</Tag>
              : <Tag color="green">全部处理完毕</Tag>}
          >
            <List
              size="small"
              dataSource={todoItems}
              renderItem={(item) => (
                <List.Item
                  style={{ padding: '10px 0', cursor: 'pointer' }}
                  onClick={() => navigate(item.to)}
                  extra={
                    <Space size={6}>
                      <Text strong style={{ fontSize: 16, color: item.count > 0 ? '#ff4d4f' : '#bfbfbf' }}>{item.count}</Text>
                      <RightOutlined style={{ fontSize: 10, color: '#bfbfbf' }} />
                    </Space>
                  }
                >
                  <Text>{item.label}</Text>
                </List.Item>
              )}
            />
          </Card>
        </Col>
        <Col xs={24} lg={10}>
          <Card title="我的课程（学生数）" size="small" style={{ height: '100%' }}>
            {courseChartData.length > 0
              ? <ReactECharts option={courseOption} style={{ height: 240 }} notMerge lazyUpdate />
              : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无进行中的课程" style={{ padding: '40px 0' }} />}
          </Card>
        </Col>
      </Row>

      {/* ③ 我的课程 · 考勤录入 + 活动 */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={14}>
          <Card
            title="我的课程 · 考勤录入"
            size="small"
            style={{ height: '100%' }}
            extra={att.total > 0
              ? <Tooltip title="到课率含迟到"><Tag color="blue">到课率 {att.attendance_rate}%</Tag></Tooltip>
              : null}
          >
            {attByCourse.length === 0
              ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无课次数据" style={{ padding: '40px 0' }} />
              : <ReactECharts option={attendanceOption} style={{ height: 232 }} notMerge lazyUpdate />}
          </Card>
        </Col>
        <Col xs={24} lg={10}>
          <Card title={<><CalendarOutlined style={{ marginRight: 6 }} />活动组织</>} size="small" style={{ height: '100%' }}>
            <Row gutter={[12, 12]}>
              <Col span={8}>
                <Statistic title="今日" value={activities.today} styles={{ content: { fontSize: 20, color: '#1890ff' } }} />
              </Col>
              <Col span={8}>
                <Statistic title="本周" value={activities.this_week} styles={{ content: { fontSize: 20, color: '#52c41a' } }} />
              </Col>
              <Col span={8}>
                <Statistic title="总计" value={activities.total} styles={{ content: { fontSize: 20, color: '#8c8c8c' } }} />
              </Col>
            </Row>
            <Button size="small" style={{ marginTop: 16 }} onClick={() => navigate('/activities')}>查看活动</Button>
          </Card>
        </Col>
      </Row>

      {/* ④ 教学运行：作业 / 课题（尚未录入时显示占位，接口结构已留好） */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={12}>
          <Card
            title="作业提交情况"
            size="small"
            style={{ height: '100%' }}
            extra={hw.pending_grading > 0 ? <Tag color="red">{hw.pending_grading} 份待批</Tag> : null}
          >
            {hw.total === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={<Text type="secondary">暂无作业数据<br />布置作业后自动统计</Text>}
                style={{ padding: '28px 0' }}
              />
            ) : (
              <>
                <Row gutter={8}>
                  <Col span={8}><Statistic title="作业份数" value={hw.total} styles={{ content: { fontSize: 22 } }} /></Col>
                  <Col span={8}><Statistic title="已提交" value={hw.submissions} styles={{ content: { fontSize: 22, color: '#1677ff' } }} /></Col>
                  <Col span={8}><Statistic title="已批阅" value={hw.graded} styles={{ content: { fontSize: 22, color: '#52c41a' } }} /></Col>
                </Row>
                <div style={{ marginTop: 12 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>提交率（应缴 {hw.expected} 人次）</Text>
                  <Progress percent={hw.submission_rate} size="small" strokeColor="#1677ff" />
                </div>
              </>
            )}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card
            title="课题研究进度"
            size="small"
            style={{ height: '100%' }}
            extra={research.pending_review > 0 ? <Tag color="red">{research.pending_review} 份待审</Tag> : null}
          >
            {research.total === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={<Text type="secondary">暂无课题数据<br />发布课题后自动统计</Text>}
                style={{ padding: '28px 0' }}
              />
            ) : (
              <Row gutter={8}>
                <Col span={8}><Statistic title="课题总数" value={research.total} styles={{ content: { fontSize: 22 } }} /></Col>
                <Col span={8}><Statistic title="进行中" value={research.ongoing} styles={{ content: { fontSize: 22, color: '#1677ff' } }} /></Col>
                <Col span={8}><Statistic title="已结题" value={research.finished} styles={{ content: { fontSize: 22, color: '#52c41a' } }} /></Col>
              </Row>
            )}
          </Card>
        </Col>
      </Row>

      {/* ⑤ 我的课程 */}
      <Card
        title={<><BookOutlined style={{ marginRight: 6 }} />我的课程（{myCourses.length}）</>}
        size="small"
        style={{ marginTop: 16 }}
      >
        {myCourses.length > 0 ? (
          <List
            grid={{ gutter: 16, xs: 1, sm: 2, md: 3, lg: 4 }}
            dataSource={myCourses}
            renderItem={(course) => (
              <List.Item>
                <Card size="small" hoverable onClick={() => navigate(`/courses/${course.id}`)}>
                  <Text strong ellipsis style={{ display: 'block' }}>{course.name}</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>{course.branch_name || '—'}</Text>
                  <div style={{ marginTop: 8 }}>
                    <Tag color="blue">{course.student_count} 名学生</Tag>
                    {course.pending_homework_count > 0 && <Tag color="red">{course.pending_homework_count} 份待批</Tag>}
                  </div>
                </Card>
              </List.Item>
            )}
          />
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无进行中的课程" />
        )}
      </Card>
    </>
  );
};

export default TeacherOverview;
